import React, { useState, useEffect, useCallback } from 'react';
import { Star, Download, RefreshCw } from 'lucide-react';
import apiService from '../apiService';
import { exportToCSV } from '../utils/adminHelpers';
import CityFilter, { useCities, cityLabel } from '../components/CityFilter';
import { loadCriteria, tagMeta, stars, fmtDate, TARGET_LABELS, CONTEXT_LABELS, STATUS_META } from '../utils/reviewLabels';

// Filtres « cible » proposés (valeurs comprises par /reviews/admin)
const TARGET_FILTERS = [
  { value: '', label: 'Toutes les cibles' },
  { value: 'driver', label: 'Chauffeurs & livreurs' },
  { value: 'driver_taxi', label: '— Chauffeurs (courses)' },
  { value: 'deliverer', label: '— Livreurs' },
  { value: 'rental_driver', label: '— Chauffeurs (location)' },
  { value: 'client', label: 'Clients' },
  { value: 'supplier_any', label: 'Commerces & établissements' },
  { value: 'supplier', label: '— Commerces (livraison)' },
  { value: 'product', label: '— Produits' },
  { value: 'lodging', label: '— Hébergements' },
  { value: 'beauty_salon', label: '— Instituts de beauté' },
  { value: 'rental_agency', label: '— Agences de location' },
];
const PERIODS = [
  { value: '', label: 'Toute la période' }, { value: '7', label: '7 derniers jours' }, { value: '30', label: '30 derniers jours' }, { value: '90', label: '90 derniers jours' },
];
const LIMIT = 25;

/**
 * Page « Avis » unifiée : toutes les verticales, les deux sens, filtres, modération.
 * `preset` fige certains filtres (ex. { target_type: 'lodging' } depuis le menu Hébergement).
 */
export default function ReviewsPage({ preset = {}, title = 'Avis' }) {
  const [filters, setFilters] = useState({ target_type: '', reviewer_type: '', city_slug: '', period: '', min: '', max: '', status: '', search: '', ...preset });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ reviews: [], total: 0, pages: 1, stats: null });
  const [loading, setLoading] = useState(true);
  const [criteria, setCriteria] = useState(null);
  const [busy, setBusy] = useState(null);
  const cities = useCities();

  useEffect(() => { loadCriteria().then(setCriteria); }, []);
  useEffect(() => { setPage(1); }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Paramètres dans l'URL : apiService.get de l'admin ignore l'objet de paramètres.
      const q = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      for (const k of ['target_type', 'reviewer_type', 'city_slug', 'min', 'max', 'status', 'search']) if (filters[k]) q.set(k, filters[k]);
      if (filters.period) q.set('from', new Date(Date.now() - Number(filters.period) * 86400000).toISOString());
      const res = await apiService.get(`/reviews/admin?${q.toString()}`, { userType: 'admin' });
      if (res?.success) setData(res);
    } catch (e) { console.error('Erreur avis:', e); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => {
    const t = setTimeout(load, filters.search ? 350 : 0); // temporisation sur la recherche
    return () => clearTimeout(t);
  }, [load, filters.search]);

  const set = (k) => (e) => setFilters(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const moderate = async (r, moderation_status) => {
    const note = moderation_status === 'published' ? '' : (window.prompt(`Motif (facultatif) — ${moderation_status === 'flagged' ? 'masquer' : 'retirer'} cet avis :`, '') ?? null);
    if (note === null) return;
    setBusy(r.review_id);
    try {
      await apiService.patch(`/reviews/admin/${r.review_id}/moderate`, { moderation_status, note }, { userType: 'admin' });
      await load();
    } catch (e) { alert(e?.error || e?.message || 'Erreur'); }
    finally { setBusy(null); }
  };
  const remove = async (r) => {
    if (!window.confirm('Supprimer définitivement cet avis ? La moyenne de la cible sera recalculée.')) return;
    setBusy(r.review_id);
    try { await apiService.delete(`/reviews/admin/${r.review_id}`, { userType: 'admin' }); await load(); }
    catch (e) { alert(e?.error || e?.message || 'Erreur'); }
    finally { setBusy(null); }
  };

  const st = data.stats;
  const dist = st?.distribution || {};
  const maxDist = Math.max(1, ...Object.values(dist));
  const sel = { padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #E9EBEF', fontSize: '0.85rem', background: 'white' };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Star size={28} /> {title}</h1>
        <div className="page-actions">
          <button className="btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}><RefreshCw size={16} /> Actualiser</button>
          <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={() => exportToCSV('avis', data.reviews.map(r => ({
              Date: fmtDate(r.created_at), Activité: CONTEXT_LABELS[r.context_type] || r.context_type, Auteur: r.reviewer_name || '—',
              Cible: `${TARGET_LABELS[r.target_type] || r.target_type} · ${r.target_name || r.target_id}`, Note: r.rating,
              Critères: (r.tags || []).map(t => tagMeta(criteria?.map, r.target_type, t).label).join(', '), Commentaire: r.comment || '',
              Réponse: r.response?.text || '', Statut: STATUS_META[r.moderation_status]?.label || r.moderation_status, Ville: cityLabel(cities, r.city_slug),
            })))}>
            <Download size={16} /> Exporter CSV
          </button>
        </div>
      </div>

      {/* Statistiques */}
      {st && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', background: 'white', borderRadius: 12, padding: '1rem 1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '1rem' }}>
          <div style={{ textAlign: 'center', minWidth: 120 }}>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#C9350F' }}>⭐ {st.average != null ? st.average.toFixed(2) : '—'}</div>
            <div style={{ fontSize: '0.8rem', color: '#5C636E' }}>{st.total} avis</div>
          </div>
          <div style={{ flex: 1, minWidth: 200, maxWidth: 420 }}>
            {[5, 4, 3, 2, 1].map(n => (
              <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem', color: '#5C636E' }}>
                <span style={{ width: 12 }}>{n}</span>
                <div style={{ flex: 1, height: 8, background: '#F4F5F7', borderRadius: 4 }}><div style={{ width: `${((dist[n] || 0) / maxDist) * 100}%`, height: 8, background: n <= 2 ? '#DC2626' : '#FFB900', borderRadius: 4 }} /></div>
                <span style={{ width: 30, textAlign: 'right' }}>{dist[n] || 0}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#5C636E' }}>
            Notes ≤ 2 : <strong style={{ color: '#991B1B' }}>{(dist[1] || 0) + (dist[2] || 0)}</strong>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        {!preset.target_type && (
          <select style={sel} value={filters.target_type} onChange={set('target_type')}>{TARGET_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
        )}
        <select style={sel} value={filters.reviewer_type} onChange={set('reviewer_type')}>
          <option value="">Tous les auteurs</option><option value="client">Donnés par des clients</option><option value="driver">Donnés par des chauffeurs</option>
        </select>
        <CityFilter value={filters.city_slug} onChange={set('city_slug')} cities={cities} className="" style={sel} />
        <select style={sel} value={filters.period} onChange={set('period')}>{PERIODS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
        <select style={sel} value={filters.min} onChange={set('min')}>
          <option value="">Toutes les notes</option><option value="4">4 et 5 ★</option><option value="3">3 ★ et plus</option>
        </select>
        <select style={sel} value={filters.max} onChange={set('max')}>
          <option value="">—</option><option value="2">≤ 2 ★ (à surveiller)</option><option value="3">≤ 3 ★</option>
        </select>
        <select style={sel} value={filters.status} onChange={set('status')}>
          <option value="">Tous les statuts</option><option value="published">Publiés</option><option value="flagged">Masqués</option><option value="removed">Retirés</option>
        </select>
        <input style={{ ...sel, minWidth: 220 }} placeholder="Nom, ID public, téléphone, commentaire…" value={filters.search} onChange={set('search')} />
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement...</p></div>
      ) : (
        <div style={{ background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                  {['Date', 'Auteur', 'Cible', 'Note', 'Critères & commentaire', 'Ville', 'Statut', 'Actions'].map(h => (
                    <th key={h} style={{ padding: 12, textAlign: 'left', fontWeight: 600, color: '#5C636E', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.reviews.length === 0 && (
                  <tr><td colSpan="8" style={{ padding: '2rem', textAlign: 'center', color: '#9AA0A8' }}>Aucun avis pour ces critères</td></tr>
                )}
                {data.reviews.map(r => {
                  const sm = STATUS_META[r.moderation_status] || STATUS_META.published;
                  const isBusy = busy === r.review_id;
                  return (
                    <tr key={r.review_id} style={{ borderTop: '1px solid #F4F5F7', opacity: r.moderation_status === 'published' ? 1 : 0.65 }}>
                      <td style={{ padding: 12, whiteSpace: 'nowrap', color: '#5C636E', fontSize: '0.8rem' }}>
                        {fmtDate(r.created_at)}<div style={{ color: '#9AA0A8' }}>{CONTEXT_LABELS[r.context_type] || r.context_type}</div>
                      </td>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>{r.reviewer_name || (r.reviewer_type === 'driver' ? 'Chauffeur' : 'Client')}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9AA0A8' }}>{r.reviewer_type === 'driver' ? 'Chauffeur' : 'Client'}{r.reviewer_public_id ? ` · ${r.reviewer_public_id}` : ''}</div>
                      </td>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>{r.target_name || r.target_id}</div>
                        <div style={{ fontSize: '0.75rem', color: '#9AA0A8' }}>{TARGET_LABELS[r.target_type] || r.target_type}{r.target_public_id ? ` · ${r.target_public_id}` : ''}{r.target_type === 'product' && r.supplier_name ? ` · ${r.supplier_name}` : ''}</div>
                      </td>
                      <td style={{ padding: 12, whiteSpace: 'nowrap', fontWeight: 700, color: r.rating <= 2 ? '#991B1B' : '#B45309' }}>{stars(r.rating)}<div style={{ fontSize: '0.75rem', fontWeight: 500 }}>{r.rating}/5</div></td>
                      <td style={{ padding: 12, maxWidth: 360 }}>
                        {r.tags?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: r.comment ? 4 : 0 }}>
                            {r.tags.map(tag => { const m = tagMeta(criteria?.map, r.target_type, tag); return <span key={tag} style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', background: m.positive === false ? '#FEE2E2' : '#ECFDF5', color: m.positive === false ? '#991B1B' : '#166534' }}>{m.label}</span>; })}
                          </div>
                        )}
                        {r.comment && <div style={{ color: '#414852', fontStyle: 'italic', fontSize: '0.82rem' }}>« {r.comment} »</div>}
                        {r.response?.text && <div style={{ color: '#166534', fontSize: '0.78rem', marginTop: 2 }}>↳ {r.response.text}</div>}
                        {r.moderation_note && <div style={{ color: '#B45309', fontSize: '0.75rem', marginTop: 2 }}>Modération : {r.moderation_note}</div>}
                      </td>
                      <td style={{ padding: 12, color: '#5C636E', whiteSpace: 'nowrap' }}>{cityLabel(cities, r.city_slug)}</td>
                      <td style={{ padding: 12 }}><span style={{ padding: '3px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, background: sm.bg, color: sm.color, whiteSpace: 'nowrap' }}>{sm.label}</span></td>
                      <td style={{ padding: 12, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {r.moderation_status === 'published' ? (
                            <>
                              <button disabled={isBusy} onClick={() => moderate(r, 'flagged')} title="Masquer (ne compte plus dans la moyenne)" style={btnMini('#B45309', '#FEF3C7')}>Masquer</button>
                              <button disabled={isBusy} onClick={() => moderate(r, 'removed')} title="Retirer" style={btnMini('#991B1B', '#FEE2E2')}>Retirer</button>
                            </>
                          ) : (
                            <button disabled={isBusy} onClick={() => moderate(r, 'published')} style={btnMini('#166534', '#ECFDF5')}>Publier</button>
                          )}
                          <button disabled={isBusy} onClick={() => remove(r)} title="Supprimer définitivement" style={btnMini('#414852', '#F4F5F7')}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.pages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '1rem', borderTop: '1px solid #E9EBEF' }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={pager(page <= 1)}>← Précédent</button>
              <span style={{ fontSize: '0.85rem', color: '#5C636E' }}>Page {page} / {data.pages} · {data.total} avis</span>
              <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} style={pager(page >= data.pages)}>Suivant →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const btnMini = (color, bg) => ({ padding: '4px 10px', borderRadius: 8, border: 'none', background: bg, color, fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' });
const pager = (disabled) => ({ padding: '6px 14px', borderRadius: 8, border: '1px solid #E9EBEF', background: disabled ? '#F4F5F7' : 'white', color: disabled ? '#DCE0E6' : '#C9350F', cursor: disabled ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.82rem' });
